/**
 * FEET + INCHES INPUT COMPONENT
 * 
 * Mobile-optimized dual input for tank gauge readings.
 * Pumpers read gauges in feet + inches, this component lets them
 * enter exactly what they see without doing mental math.
 * 
 * IMPORTANT: This component outputs TOTAL INCHES to maintain
 * backwards compatibility with all existing code.
 * 
 * @example
 * <FeetInchesInput
 *   value="55"  // Total inches from state
 *   onChange={(totalInches) => handleChange(totalInches)}
 *   label="Top Gauge"
 * />
 */

import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '../ui/utils';
import { 
  inchesToFeetInches, 
  feetInchesToTotalInches
} from '../../utils/gaugeConversion';

interface FeetInchesInputProps {
  /** Current value in TOTAL INCHES (string for compatibility with existing code) */
  value: string;
  /** Called with TOTAL INCHES when either field changes */
  onChange: (totalInches: string) => void;
  /** Optional label shown above inputs */
  label?: string;
  /** Placeholder for feet input */
  feetPlaceholder?: string;
  /** Placeholder for inches input */
  inchesPlaceholder?: string;
  /** Dark mode styling */
  isDarkMode?: boolean;
  /** Disable inputs */
  disabled?: boolean;
  /** Read-only mode */
  readOnly?: boolean;
  /** Additional class for container */
  className?: string;
  /** Input size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Show the total inches calculation */
  showTotal?: boolean;
  /** Error message to display */
  error?: string;
  /** Custom input class override */
  inputClassName?: string;
  /** Maximum allowed total inches (for validation display) */
  maxTotalInches?: number;
  /** Sync status for visual feedback */
  syncStatus?: 'pristine' | 'local' | 'syncing' | 'synced' | 'submitted' | 'error' | 'saving';
}

export function FeetInchesInput({
  value,
  onChange,
  label,
  feetPlaceholder = '0',
  inchesPlaceholder = '0',
  isDarkMode = false,
  disabled = false,
  readOnly = false,
  className,
  size = 'md',
  showTotal = true,
  error,
  inputClassName,
  maxTotalInches,
  syncStatus = 'pristine',
}: FeetInchesInputProps) {
  // Parse incoming total inches to feet + inches for display
  const parsed = inchesToFeetInches(value);
  
  // Local state for the two input fields
  const [feetValue, setFeetValue] = useState(parsed.feet > 0 ? String(parsed.feet) : '');
  const [inchesValue, setInchesValue] = useState(parsed.inches > 0 ? String(parsed.inches) : '');
  
  // Sync local state when external value changes (e.g., loading saved data)
  useEffect(() => {
    const newParsed = inchesToFeetInches(value);
    // Only update if significantly different (avoid loops)
    const currentTotal = feetInchesToTotalInches(feetValue, inchesValue);
    const newTotal = parseFloat(value) || 0;
    
    if (Math.abs(currentTotal - newTotal) > 0.1) {
      setFeetValue(newParsed.feet > 0 ? String(newParsed.feet) : '');
      setInchesValue(newParsed.inches > 0 ? String(newParsed.inches) : '');
    }
  }, [value]);
  
  // Handle feet change
  const handleFeetChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    
    // Allow empty or valid numeric input
    if (rawValue === '' || /^\d*$/.test(rawValue)) {
      setFeetValue(rawValue);
      
      // Calculate and emit total inches
      const totalInches = feetInchesToTotalInches(rawValue, inchesValue);
      onChange(totalInches > 0 ? String(totalInches) : '');
    }
  }, [inchesValue, onChange]);
  
  // Handle inches change
  const handleInchesChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    
    // Allow empty, or valid numeric input (including decimals)
    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
      // Validate inches is < 12
      const numValue = parseFloat(rawValue);
      if (rawValue !== '' && !isNaN(numValue) && numValue >= 12) {
        // Auto-convert: if they type 12+, convert to feet
        const extraFeet = Math.floor(numValue / 12);
        const remainingInches = numValue % 12;
        const newFeet = (parseFloat(feetValue) || 0) + extraFeet;
        
        setFeetValue(String(newFeet));
        setInchesValue(remainingInches > 0 ? String(remainingInches) : '');
        
        const totalInches = feetInchesToTotalInches(newFeet, remainingInches);
        onChange(totalInches > 0 ? String(totalInches) : '');
      } else {
        setInchesValue(rawValue);
        
        // Calculate and emit total inches
        const totalInches = feetInchesToTotalInches(feetValue, rawValue);
        onChange(totalInches > 0 ? String(totalInches) : '');
      }
    }
  }, [feetValue, onChange]);
  
  // Size variants
  const sizeClasses = {
    sm: 'px-2 py-1.5 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base',
  };
  
  const labelSizeClasses = {
    sm: 'text-[8px]',
    md: 'text-[10px]',
    lg: 'text-xs',
  };
  
  // Get sync-based styling
  const getSyncStatusClass = () => {
    if (error) return 'border-red-500 ring-1 ring-red-500';
    
    const hasValue = totalInches > 0;
    if (!hasValue) return isDarkMode ? 'border-slate-700' : 'border-gray-200';
    
    switch (syncStatus) {
      case 'submitted':
        return isDarkMode
          ? 'border-emerald-500 ring-1 ring-emerald-500/50 bg-emerald-900/30'
          : 'border-emerald-500 ring-1 ring-emerald-500/50 bg-emerald-50';
      case 'synced':
        return isDarkMode
          ? 'border-blue-500/50 ring-1 ring-blue-500/50 bg-blue-900/20'
          : 'border-blue-500/50 ring-1 ring-blue-500/50 bg-blue-50';
      case 'local':
      case 'syncing':
      case 'saving':
        return isDarkMode
          ? 'border-orange-500/50 ring-1 ring-orange-500/50 bg-orange-900/20'
          : 'border-orange-500/50 ring-1 ring-orange-500/50 bg-orange-50';
      case 'error':
        return isDarkMode
          ? 'border-red-500/50 ring-1 ring-red-500/50 bg-red-900/20'
          : 'border-red-500/50 ring-1 ring-red-500/50 bg-red-50';
      default:
        // Default filled state
        return hasValue
          ? (isDarkMode ? 'border-emerald-500/50 bg-emerald-900/20' : 'border-emerald-500/50 bg-emerald-50')
          : (isDarkMode ? 'border-slate-700' : 'border-gray-200');
    }
  };
  
  // Calculate total for display
  const totalInches = feetInchesToTotalInches(feetValue, inchesValue);
  const isOverMax = maxTotalInches !== undefined && totalInches > maxTotalInches;
  const syncStatusClass = getSyncStatusClass();
  
  return (
    <div className={cn("space-y-1", className)}>
      {label && (
        <label className={cn(
          "block uppercase font-medium",
          labelSizeClasses[size],
          isDarkMode ? "text-slate-500" : "text-gray-400"
        )}>
          {label}
        </label>
      )}
      
      <div className="flex items-center gap-1.5">
        {/* Feet Input */}
        <div className="flex-1 min-w-0">
          <div className="relative">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder={feetPlaceholder}
              value={feetValue}
              onChange={handleFeetChange}
              disabled={disabled}
              readOnly={readOnly}
              className={cn(
                "w-full rounded-xl border focus:ring-2 transition-all text-center font-medium",
                sizeClasses[size],
                syncStatusClass,
                isDarkMode
                  ? "focus:border-indigo-500 focus:ring-indigo-900 text-white placeholder-slate-600"
                  : "focus:border-indigo-500 focus:ring-indigo-100 text-slate-900 placeholder-gray-400",
                (disabled || readOnly) && "opacity-75 cursor-not-allowed",
                inputClassName
              )}
            />
            <span className={cn(
              "absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium pointer-events-none",
              isDarkMode ? "text-slate-500" : "text-gray-400"
            )}>
              ft
            </span>
          </div>
        </div>
        
        {/* Inches Input */}
        <div className="flex-1 min-w-0">
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              placeholder={inchesPlaceholder}
              value={inchesValue}
              onChange={handleInchesChange}
              disabled={disabled}
              readOnly={readOnly}
              className={cn(
                "w-full rounded-xl border focus:ring-2 transition-all text-center font-medium",
                sizeClasses[size],
                syncStatusClass,
                isDarkMode
                  ? "focus:border-indigo-500 focus:ring-indigo-900 text-white placeholder-slate-600"
                  : "focus:border-indigo-500 focus:ring-indigo-100 text-slate-900 placeholder-gray-400",
                (disabled || readOnly) && "opacity-75 cursor-not-allowed",
                inputClassName
              )}
            />
            <span className={cn(
              "absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium pointer-events-none",
              isDarkMode ? "text-slate-500" : "text-gray-400"
            )}>
              in
            </span>
          </div>
        </div>
        
        {/* Total Display */}
        {showTotal && totalInches > 0 && (
          <div className={cn(
            "text-xs font-mono whitespace-nowrap px-2 py-1 rounded-lg",
            isOverMax 
              ? "bg-red-500/20 text-red-500"
              : isDarkMode ? "bg-slate-800 text-slate-400" : "bg-gray-100 text-gray-500"
          )}>
            ={totalInches}"
          </div>
        )}
      </div>
      
      {/* Error message or max warning */}
      {error && (
        <p className="text-[10px] text-red-500 mt-1">{error}</p>
      )}
      {!error && isOverMax && (
        <p className="text-[10px] text-red-500 mt-1">
          Max: {maxTotalInches}" ({Math.floor(maxTotalInches! / 12)}' {maxTotalInches! % 12}")
        </p>
      )}
    </div>
  );
}

export default FeetInchesInput;
